import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { authService } from "./userService";
import { toast } from "react-toastify";

// Helper to validate JWT token
const isValidToken = (token) => {
  return typeof token === "string" && token.startsWith("eyJ");
};

// Register User
export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.register(userData);
       if (response.token && !isValidToken(response.token)) {
        throw new Error("Invalid token received");
      }
      console.log("Register user response:", response);
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Registration failed";
      console.error("Register user error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Register Account
export const registerAccount = createAsyncThunk(
  "auth/registerAccount",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.endUserRegister(userData);
      // Only validate token if it exists in the response
      if (response.token && !isValidToken(response.token)) {
        throw new Error("Invalid token received");
      }
      console.log("Register account response:", response);
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Registration failed";
      console.error("Register account error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Register End User
export const registerEndUser = createAsyncThunk(
  "auth/registerEndUser",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.endUserRegister(userData);
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Registration failed";
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Request OTP (Send OTP)
export const loginWithOTP = createAsyncThunk(
  "auth/loginWithOTP",
  async (email, thunkAPI) => {
    try {
      const response = await authService.login(email);
      console.log("Login with OTP response:", response);
      return response; // Expect { message: "OTP sent" }
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to send OTP";
      console.error("Login with OTP error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Login with End User
export const loginWithEndUser = createAsyncThunk(
  "auth/loginWithEndUser",
  async (email, thunkAPI) => {
    try {
      const response = await authService.endUserLogin(email);
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to send OTP";
      console.error("Login with OTP error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Login with Channel
export const loginWithChannel = createAsyncThunk(
  "auth/loginWithChannel",
  async (channelData, thunkAPI) => {
    try {
      const response = await authService.channeLogin(channelData);
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to send OTP";
      console.error("Login with OTP error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Verify OTP (Login)
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, thunkAPI) => {
    try {
      const response = await authService.otp({ email, otp });
      console.log("Verify OTP response:", response);
      if (!isValidToken(response.token)) {
        throw new Error("Invalid token received");
      }
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "OTP verification failed";
      console.error("Verify OTP error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// End User OTP
export const endUserOtp = createAsyncThunk(
  "auth/endUserOtp",
  async ({ email, otp }, thunkAPI) => {
    try {
      const response = await authService.endOtp({ email, otp });
      console.log("Verify OTP response:", response);
      if (!isValidToken(response.token)) {
        throw new Error("Invalid token received");
      }
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "OTP verification failed";
      console.error("Verify OTP error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Channel User OTP
export const channelUserOtp = createAsyncThunk(
  "auth/channelUserOtp",
  async (channelOtpData, thunkAPI) => {
    try {
      const response = await authService.channelOtp(channelOtpData);
      console.log("Verify OTP response:", response);
      if (!isValidToken(response.token)) {
        throw new Error("Invalid token received");
      }
      return response;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "OTP verification failed";
      console.error("Verify OTP error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Get User Wishlist
export const getuserProductWishlist = createAsyncThunk(
  "user/wishlist",
  async (_, thunkAPI) => {
    try {
      return await authService.getUserWislist();
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to fetch wishlist";
      console.error("Get wishlist error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Add Product to Cart
export const addProdToCart = createAsyncThunk(
  "user/cart/add",
  async (cartData, thunkAPI) => {
    try {
      return await authService.addToCart(cartData);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to add to cart";
      console.error("Add to cart error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Get User Cart
export const getUserCart = createAsyncThunk(
  "user/cart/get",
  async (_, thunkAPI) => {
    try {
      return await authService.getCart();
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to fetch cart";
      console.error("Get cart error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Delete User Cart
export const deleteUserCart = createAsyncThunk(
  "user/cart/delete",
  async (_, thunkAPI) => {
    try {
      return await authService.emptyCart();
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to delete cart";
      console.error("Delete cart error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Get Orders
export const getOrders = createAsyncThunk(
  "user/orders/get",
  async (_, thunkAPI) => {
    try {
      return await authService.getUserOrders();
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to fetch orders";
      console.error("Get orders error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Delete Cart Product
export const deleteCartProduct = createAsyncThunk(
  "user/cart/product/delete",
  async (data, thunkAPI) => {
    try {
      return await authService.removeProductFromCart(data);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to delete product from cart";
      console.error("Delete cart product error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Create Order
export const createAnOrder = createAsyncThunk(
  "user/cart/create-order",
  async (orderDetail, thunkAPI) => {
    try {
      return await authService.createOrder(orderDetail);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to create order";
      console.error("Create order error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Update Cart Product
export const updateCartProduct = createAsyncThunk(
  "user/cart/product/update",
  async (cartDetail, thunkAPI) => {
    try {
      return await authService.updateProductFromCart(cartDetail);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to update cart product";
      console.error("Update cart product error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Update Profile
export const updateProfile = createAsyncThunk(
  "user/profile/update",
  async (data, thunkAPI) => {
    try {
      return await authService.updateUser(data);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to update profile";
      console.error("Update profile error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Forgot Password Token
export const forgotPasswordToken = createAsyncThunk(
  "user/password/token",
  async (data, thunkAPI) => {
    try {
      return await authService.forgotPasswordToken(data);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to send password reset token";
      console.error("Forgot password token error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Reset Password
export const resetPassword = createAsyncThunk(
  "user/password/reset",
  async (data, thunkAPI) => {
    try {
      return await authService.resetPass(data);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to reset password";
      console.error("Reset password error:", message);
      return thunkAPI.rejectWithValue({ message });
    }
  }
);

// Reset State Action
export const resetState = createAction("Reset_all");

// Initial State
const tokenFromLocalStorage = localStorage.getItem("user_website") || null;

const initialState = {
  token: tokenFromLocalStorage,
  user: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
  wishlist: [],
  createdUser: null,
  otpRequested: false,
  cartProducts: [],
  cartProduct: null,
  deletedCartProduct: null,
  updatedCartProduct: null,
  orderedProduct: null,
  getorderedProduct: null,
  updatedUser: null,
  pass: null,
  deletedCart: null,
};

// Auth Slice
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
      localStorage.removeItem("user_website");
      toast.success("Logged out successfully");
    },
  },
  extraReducers: (builder) => {
    builder
      // Register User
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.createdUser = action.payload;
        state.token = action.payload.token;
        state.user = action.payload.user || null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
      })
      // Register Account
      .addCase(registerAccount.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(registerAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.createdUser = action.payload;
        // Only set token and user if they exist in the response
        state.token = action.payload.token || state.token;
        state.user = action.payload.user || state.user;
        // if (state.isSuccess) {
        //   toast.success("Account Created Successfully");
        // }
      })
      .addCase(registerAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Request OTP (Send OTP)
      .addCase(loginWithOTP.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(loginWithOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.otpRequested = true;
      })
      .addCase(loginWithOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Verify OTP (Login)
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.token = action.payload.token;
        state.user = action.payload.user || null;
        state.otpRequested = false;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.otpRequested = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Get User Wishlist
      .addCase(getuserProductWishlist.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getuserProductWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.wishlist = action.payload.data?.products || [];
      })
      .addCase(getuserProductWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Add Product to Cart
      .addCase(addProdToCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addProdToCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.cartProduct = action.payload;
        if (state.isSuccess) {
          toast.success("Product Added To Cart");
        }
      })
      .addCase(addProdToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Get User Cart
      .addCase(getUserCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.cartProducts = action.payload;
      })
      .addCase(getUserCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Delete Cart Product
      .addCase(deleteCartProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteCartProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedCartProduct = action.payload;
        if (state.isSuccess) {
          toast.success("Product Deleted From Cart Successfully!");
        }
      })
      .addCase(deleteCartProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Update Cart Product
      .addCase(updateCartProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCartProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.updatedCartProduct = action.payload;
        if (state.isSuccess) {
          toast.success("Product Updated Successfully!");
        }
      })
      .addCase(updateCartProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Create Order
      .addCase(createAnOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createAnOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.orderedProduct = action.payload;
        if (state.isSuccess) {
          toast.success("Order created Successfully!");
        }
      })
      .addCase(createAnOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Get Orders
      .addCase(getOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.getorderedProduct = action.payload;
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.updatedUser = action.payload;
        state.user = {
          ...state.user,
          firstname: action.payload.firstname,
          lastname: action.payload.lastname,
          email: action.payload.email,
          mobile: action.payload.mobile,
        };
        if (state.isSuccess) {
          toast.success("Profile Updated Successfully!");
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Forgot Password Token
      .addCase(forgotPasswordToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(forgotPasswordToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.token = action.payload.token || state.token;
        if (state.isSuccess) {
          toast.success("Forgot Password Email Sent Successfully!");
        }
      })
      .addCase(forgotPasswordToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.pass = action.payload;
        if (state.isSuccess) {
          toast.success("Password changed Successfully!");
        }
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Delete User Cart
      .addCase(deleteUserCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUserCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedCart = action.payload;
        state.cartProducts = [];
        if (state.isSuccess) {
          toast.success("Cart Cleared Successfully!");
        }
      })
      .addCase(deleteUserCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload.message;
        if (state.isError) {
          toast.error(action.payload.message);
        }
      })
      // Reset State
      .addCase(resetState, () => initialState);
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;